import { useState, useEffect } from 'react';
import { workflowApi } from '../services/api.js';
import { Alert, AlertDescription } from '../components/ui/Alert.jsx';
import { Check, AlertCircle, Loader, Settings, Workflow, ChevronDown } from 'lucide-react';

export function ConfigurationsList() {
  const [activeTab, setActiveTab] = useState('system');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [systemConfig, setSystemConfig] = useState(null);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [workflowConfig, setWorkflowConfig] = useState(null);
  const [templates, setTemplates] = useState([]);
  const CONFIG_ID = '69b8f3fa-6808-45ea-8b04-f8b80b66772b';

  useEffect(() => {
    fetchSystemConfig();
    fetchTemplates();
  }, []);

  const fetchSystemConfig = async () => {
    try {
      setLoading(true);
      const response = await workflowApi.getSystemConfiguration(CONFIG_ID);
      setSystemConfig(response.data?.value || {});
    } catch (_error) {
      // Silently handle 404 and other errors by setting empty config
      setSystemConfig({});
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await workflowApi.getTemplates();
      setTemplates(response.data.data || []);
    } catch (_error) {
      setError('Failed to fetch templates');
    }
  };

  const fetchWorkflowConfig = async (workflowId) => {
    try {
      setLoading(true);
      const template = templates.find(t => t.id === workflowId);
      if (!template || !template.startNodes) {
        throw new Error('Invalid template structure');
      }
      
      // Create default config structure
      const defaultConfig = {
        nodes: Object.fromEntries(
          template.nodes.map(node => {
            const isStartNode = template.startNodes.includes(node.id);
            return [
              node.id,
              {
                inputs: Object.fromEntries(
                  (template.initialInputs || []).map(input => [input, ""])
                )
              }
            ];
          })
        )
      };

      try {
        // Use new endpoint structure
        const response = await workflowApi.getWorkflowConfiguration(CONFIG_ID, workflowId);
        setWorkflowConfig(response.data || defaultConfig);
      } catch (_configError) {
        setWorkflowConfig(defaultConfig);
      }
      setSelectedWorkflow(workflowId);
    } catch (_error) {
      console.error('Error fetching workflow config:', _error);
      setError(`Failed to fetch workflow configuration: ${_error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSystem = async (value) => {
    try {
      setSaving(true);
      await workflowApi.upsertSystemConfiguration(CONFIG_ID, {
        value: value,
        description: 'System configuration'
      });
      await fetchSystemConfig();
      setError(null);
    } catch (_error) {
      setError('Failed to save system configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveWorkflow = async (value) => {
    if (!selectedWorkflow) return;

    try {
      setSaving(true);
      await workflowApi.upsertWorkflowConfiguration(CONFIG_ID, selectedWorkflow, {
        nodeConfigs: value,
        description: 'Workflow configuration'
      });
      await fetchWorkflowConfig(selectedWorkflow);
      setError(null);
    } catch (_error) {
      setError('Failed to save workflow configuration');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Configurations</h1>
        <p className="text-gray-600 mt-2">Manage system-wide and workflow-specific configurations</p>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="flex" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('system')}
              className={`flex items-center px-6 py-4 text-sm font-medium border-b-2 ${
                activeTab === 'system'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Settings className="w-5 h-5 mr-2" />
              System Configuration
            </button>
            <button
              onClick={() => setActiveTab('workflow')}
              className={`flex items-center px-6 py-4 text-sm font-medium border-b-2 ${
                activeTab === 'workflow'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Workflow className="w-5 h-5 mr-2" />
              Workflow Configuration
            </button>
          </nav>
        </div>

        <div className="p-6">
          {error && (
            <Alert variant="destructive" className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {activeTab === 'system' ? (
            <SystemConfigEditor
              config={systemConfig}
              onSave={handleSaveSystem}
              saving={saving}
            />
          ) : (
            <WorkflowConfigEditor
              templates={templates}
              selectedWorkflow={selectedWorkflow}
              config={workflowConfig}
              onWorkflowSelect={fetchWorkflowConfig}
              onSave={handleSaveWorkflow}
              saving={saving}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function SystemConfigEditor({ config, onSave, saving }) {
  const [editedValue, setEditedValue] = useState('');
  const [isValidJson, setIsValidJson] = useState(true);
  const [jsonError, setJsonError] = useState(null);

  useEffect(() => {
    // For system config, just use empty object if none exists
    setEditedValue(JSON.stringify(config || {}, null, 2));
  }, [config]);

  const validateJson = (jsonString) => {
    try {
      JSON.parse(jsonString);
      setIsValidJson(true);
      setJsonError(null);
    } catch (err) {
      setIsValidJson(false);
      setJsonError(err.message);
    }
  };

  useEffect(() => {
    validateJson(editedValue);
  }, [editedValue]);

  const prettifyJson = () => {
    try {
      const parsed = JSON.parse(editedValue);
      setEditedValue(JSON.stringify(parsed, null, 2));
      setIsValidJson(true);
      setJsonError(null);
    } catch (_err) {
      setJsonError('Cannot prettify: Invalid JSON format');
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-lg font-medium text-gray-900">System-wide Settings</h2>
        <p className="mt-1 text-sm text-gray-500">
          Configure global parameters that apply across all workflows
        </p>
      </div>

      <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center space-x-4">
            <label className="text-sm font-medium text-gray-700">
              Configuration JSON
            </label>
            {isValidJson && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                <Check className="h-3 w-3 mr-1" />
                Valid JSON
              </span>
            )}
          </div>
          <button
            onClick={prettifyJson}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Format JSON
          </button>
        </div>

        <div className="space-y-4">
          <textarea
            value={editedValue}
            onChange={(e) => setEditedValue(e.target.value)}
            className={`w-full h-96 border rounded-lg p-4 font-mono text-sm focus:ring-2 focus:ring-blue-500 bg-white ${
              !isValidJson ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Enter JSON configuration"
          />
          
          {jsonError && (
            <div className="flex items-center text-sm text-red-600">
              <AlertCircle className="h-4 w-4 mr-1.5" />
              <span>JSON Error: {jsonError}</span>
            </div>
          )}
        </div>
      </div>

      <div className="mt-6 flex justify-end">
        <button
          onClick={() => onSave(JSON.parse(editedValue))}
          disabled={!isValidJson || saving}
          className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white focus:outline-none focus:ring-2 focus:ring-offset-2 ${
            isValidJson && !saving
              ? 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
              : 'bg-gray-400 cursor-not-allowed'
          }`}
        >
          {saving && <Loader className="animate-spin -ml-1 mr-2 h-4 w-4" />}
          {saving ? 'Saving Changes...' : 'Save Configuration'}
        </button>
      </div>
    </div>
  );
}

function WorkflowConfigEditor({ templates, selectedWorkflow, config, onWorkflowSelect, onSave, saving }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [nodeConfigs, setNodeConfigs] = useState({});
  const [nodeErrors, setNodeErrors] = useState({});
  const [error, setError] = useState(null);

  const filteredTemplates = templates.filter(template =>
    template.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    if (selectedWorkflow) {
      const template = templates.find(t => t.id === selectedWorkflow);
      if (!template) return;
      
      const initialConfigs = {};
      template.nodes.forEach((node) => {
        let nodeInputs = {};
        
        // Handle both WORKFLOW and regular nodes
        if (node.type === 'WORKFLOW') {
          const workflowTemplate = templates.find(t => t.id === node.referenceId);
          if (workflowTemplate?.initialInputs) {
            nodeInputs = Object.fromEntries(
              workflowTemplate.initialInputs.map(input => [
                input,
                config?.nodes?.[node.id]?.inputs?.[input] || ""
              ])
            );
          }
        } else {
          // For non-WORKFLOW nodes, preserve existing config or use empty object
          nodeInputs = config?.nodes?.[node.id]?.inputs || {};
        }
        
        initialConfigs[node.id] = {
          inputs: JSON.stringify(nodeInputs, null, 2),
          nodeName: node.name || 'Unnamed Node',
          nodeId: node.id,
          type: node.type || 'Unknown Type',
          isStartNode: template.startNodes.includes(node.id)
        };
      });

      // Single state update with all configs
      setNodeConfigs(initialConfigs);
    }
  }, [selectedWorkflow, config, templates]);

  const handleNodeConfigChange = (nodeId, value) => {
    setNodeConfigs(prev => ({
      ...prev,
      [nodeId]: {
        ...prev[nodeId],
        inputs: value
      }
    }));

    try {
      JSON.parse(value);
      setNodeErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[nodeId];
        return newErrors;
      });
    } catch (err) {
      setNodeErrors(prev => ({
        ...prev,
        [nodeId]: 'Invalid JSON format'
      }));
    }
  };

  const prettifyJson = (nodeId) => {
    try {
      const parsed = JSON.parse(nodeConfigs[nodeId].inputs);
      setNodeConfigs(prev => ({
        ...prev,
        [nodeId]: {
          ...prev[nodeId],
          inputs: JSON.stringify(parsed, null, 2)
        }
      }));
      setNodeErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[nodeId];
        return newErrors;
      });
    } catch (err) {
      setNodeErrors(prev => ({
        ...prev,
        [nodeId]: 'Cannot prettify: Invalid JSON format'
      }));
    }
  };

  const handleSave = () => {
    try {
      const combinedConfig = {
        nodes: Object.entries(nodeConfigs).reduce((acc, [nodeId, nodeConfig]) => {
          acc[nodeId] = {
            inputs: JSON.parse(nodeConfig.inputs)
          };
          return acc;
        }, {})
      };
      onSave(combinedConfig);
    } catch (_err) {
      setError('Invalid JSON in one or more nodes');
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-lg font-medium text-gray-900">Workflow Settings</h2>
        <p className="mt-1 text-sm text-gray-500">
          Configure node-specific parameters for individual workflows
        </p>
      </div>

      <div className="space-y-6">
        <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Workflow Template
          </label>
          
          <div className="relative">
            <div className="flex items-center border border-gray-300 rounded-lg bg-white">
              <div className="flex-grow">
                <input
                  type="text"
                  placeholder="Search templates..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-2 border-0 focus:ring-0 text-sm rounded-l-lg"
                />
              </div>
              <div className="relative">
                <select
                  value={selectedWorkflow || ''}
                  onChange={(e) => onWorkflowSelect(e.target.value)}
                  className="appearance-none w-full pl-4 pr-10 py-2 border-l border-gray-300 bg-transparent text-sm focus:outline-none focus:ring-0"
                >
                  <option value="">Select template...</option>
                  {filteredTemplates.map(template => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500">
                  <ChevronDown className="h-4 w-4" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {selectedWorkflow && (
          <>
            {Object.entries(nodeConfigs)
              .sort(([, a], [, b]) => {
                if (a.isStartNode === b.isStartNode) {
                  return a.nodeName.localeCompare(b.nodeName);
                }
                return b.isStartNode ? 1 : -1;
              })
              .map(([nodeId, nodeConfig]) => (
                <div key={nodeId} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-medium text-gray-900">
                          {nodeConfig.nodeName} <span className="ml-2 text-sm text-gray-500">#{nodeConfig.nodeId}</span>
                        </h3>
                        <p className="mt-1 text-sm text-gray-500">Type: {nodeConfig.type}</p>
                      </div>
                      <div className="flex items-center space-x-4">
                        <button
                          onClick={() => prettifyJson(nodeId)}
                          className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                        >
                          Format JSON
                        </button>
                        {nodeConfig.isStartNode && (
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            Start Node
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <textarea
                      value={nodeConfig.inputs}
                      onChange={(e) => handleNodeConfigChange(nodeId, e.target.value)}
                      className={`w-full h-48 border rounded-lg p-4 font-mono text-sm focus:ring-2 focus:ring-blue-500 ${
                        nodeErrors[nodeId] ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Enter node configuration"
                    />
                    {nodeErrors[nodeId] && (
                      <div className="mt-2 text-sm text-red-600">
                        <AlertCircle className="h-4 w-4 inline mr-1.5" />
                        {nodeErrors[nodeId]}
                      </div>
                    )}
                  </div>
                </div>
              ))}

            <div className="flex justify-end pt-6">
              <button
                onClick={handleSave}
                disabled={saving || Object.keys(nodeErrors).length > 0}
                className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                  !saving && Object.keys(nodeErrors).length === 0
                    ? 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
                    : 'bg-gray-400 cursor-not-allowed'
                }`}
              >
                {saving && <Loader className="animate-spin -ml-1 mr-2 h-4 w-4" />}
                {saving ? 'Saving Changes...' : 'Save All Configurations'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
} 